import { useEffect, useRef, useState } from "react";
import { BarChart3, TrendingUp, TrendingDown, Users, ShoppingCart, XCircle, Percent, RefreshCw, Info, AlertTriangle, CheckCircle2, Car, Bell, PieChart, Wallet, Download, PhoneCall, ChevronRight, ChevronDown } from "lucide-react";
import api from "../lib/api";
import MultiSelect from "../components/MultiSelect";
import PdfExportButton from "../components/PdfExportButton";
import { RefetchPill } from "../components/Loader";
import useMinVisible from "../hooks/useMinVisible";
import ProjectionKpiCard from "../components/ProjectionKpiCard";
import NuevosPorSucursal from "../components/NuevosPorSucursal";
import ScorecardInfoTip from "../components/scorecard/ScorecardInfoTip";
import { TooltipProvider } from "../components/ui/tooltip";
import TestDriveClosingTable from "../components/TestDriveClosingTable";
import BankStatusSection from "../components/BankStatusSection";
import QualificationTrendChart from "../components/QualificationTrendChart";
import TradeInCard, { TRADE_IN_ENABLED } from "../components/TradeInCard";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const DEFAULT_FILTERS = {
  year: String(new Date().getFullYear()),
  month: "Todos",
  brand: "Todas",
  business_unit: "Todas",
  executive: "Todos",
  status: "Todos",
  channel: "Todos",
  campaign: "Todas",
  // Carry-over from the pie drill-down. Not surfaced in the top filter
  // row; kept in state so drilling into a status group still round-trips.
  lead_status_group: "Todos",
  // Sucursal (showroom) — restored to the top filter row 2026-02-18 per
  // user request; still supported by every backend endpoint.
  showroom: "Todas",
  // Model is still tolerated by the backend for backward compat (older
  // bookmarks / QR-shared views may pass it) but not rendered in the
  // top filter row.
  model: "Todos",
};

const FILTER_KEY = "sales_performance_filters_v1";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("es-DO"));
// Interruptor para ocultar cambios no aprobados en producción (true = todo visible).
const PENDING_FEATURES = true;
const fmtPct = (n, digits = 1) =>
  n == null || Number.isNaN(n) ? "—" : `${Number(n).toFixed(digits)}%`;

// Delta pill used across all KPI cards. Green when positive, red when
// negative — flip via `inverted` for metrics where lower is better
// (e.g. % descalificación).
function DeltaPill({ value, inverted = false, testid }) {
  if (value == null || Number.isNaN(value)) {
    return <span className="text-xs font-bold text-gray-400" data-testid={testid}>—</span>;
  }
  const positive = value > 0;
  const good = inverted ? !positive : positive;
  const color = value === 0 ? "#6b7280" : good ? "#16a34a" : "#dc2626";
  const bg = value === 0 ? "#f1f5f9" : good ? "#dcfce7" : "#fee2e2";
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-extrabold"
      style={{ background: bg, color }}
      data-testid={testid}
    >
      <Icon size={11} strokeWidth={2.4}/>
      {value > 0 ? "+" : ""}{Number(value).toFixed(1)}%
    </span>
  );
}

// KPI card — value + tiny MoM/YoY row.
function KpiCard({ icon: Icon, label, value, sub, mom, yoy, accent, invertedDelta,
                   footnote, testid, tip }) {
  return (
    <div className="surface p-5" data-testid={testid} style={{
      borderTop: `3px solid ${accent}`,
    }}>
      <div className="flex items-start justify-between mb-3">
        <div className="kpi-icon-clean" style={{
          color: accent, boxShadow: `inset 0 0 0 1px ${accent}1f`,
        }}><Icon size={18} strokeWidth={2.25}/></div>
      </div>
      <div className="label-up mb-2 flex items-center gap-1.5">{label}{tip}</div>
      <div className="font-display font-extrabold leading-none mb-2"
           style={{ fontSize: 32, letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div className="text-xs font-semibold text-gray-500 mb-2 truncate" title={sub}>{sub}</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">MoM</span>
        <DeltaPill value={mom} inverted={invertedDelta} testid={`${testid}-mom`}/>
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 ml-1">YoY</span>
        <DeltaPill value={yoy} inverted={invertedDelta} testid={`${testid}-yoy`}/>
      </div>
      {footnote && <div className="text-[10px] text-gray-400 font-semibold mt-2 italic">{footnote}</div>}
    </div>
  );
}

// Compact bar chart for the sales trend section. Renders current year
// vs previous year as grouped bars per month using inline SVG (no chart
// library dependency, matches the ChannelPieChart/DualTrend patterns).
function SalesTrendChart({ monthly, yearCurrent, yearPrevious, activeMonth, onClickMonth }) {
  const maxV = Math.max(1, ...monthly.map((m) => Math.max(m.current || 0, m.previous || 0)));
  const W = 900;
  const H = 220;
  const PAD_L = 40, PAD_R = 10, PAD_T = 20, PAD_B = 30;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const barGroupW = chartW / monthly.length;
  const barW = Math.max(6, (barGroupW - 6) / 2);

  // Linear regression on the CURRENT-year monthly series. Skip months
  // with 0/undefined values (typically future months so they don't drag
  // the slope down to zero and mislead the eye).
  const points = monthly
    .map((m, i) => ({ i, v: Number(m.current || 0) }))
    .filter((p) => p.v > 0);
  let trendPath = "";
  let trendSlope = 0;
  if (points.length >= 2) {
    const n = points.length;
    const sumX = points.reduce((a, p) => a + p.i, 0);
    const sumY = points.reduce((a, p) => a + p.v, 0);
    const sumXY = points.reduce((a, p) => a + p.i * p.v, 0);
    const sumX2 = points.reduce((a, p) => a + p.i * p.i, 0);
    const denom = n * sumX2 - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    trendSlope = slope;
    // Draw the line only across months that have data (avoid flat tail
    // over future months that read as "we're flat" when we simply don't
    // have data yet).
    const firstI = points[0].i;
    const lastI = points[points.length - 1].i;
    const xAt = (i) => PAD_L + i * barGroupW + barGroupW / 2;
    const yAt = (val) => PAD_T + chartH - Math.max(0, Math.min(chartH, (val / maxV) * chartH));
    const x1 = xAt(firstI), y1 = yAt(intercept + slope * firstI);
    const x2 = xAt(lastI),  y2 = yAt(intercept + slope * lastI);
    trendPath = `M ${x1} ${y1} L ${x2} ${y2}`;
  }
  const trendColor = trendSlope >= 0 ? "#16a34a" : "#dc2626";

  return (
    <div className="w-full max-w-[1400px] mx-auto" data-testid="sales-trend-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
           preserveAspectRatio="xMidYMid meet"
           style={{ display: "block" }}>
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = PAD_T + chartH * (1 - f);
          return (
            <g key={f}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#e2e8f0" strokeWidth={f === 0 ? 1.5 : 1} strokeDasharray={f === 0 ? "" : "3 3"}/>
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize={10} fill="#94a3b8" fontWeight={700}>
                {Math.round(maxV * f)}
              </text>
            </g>
          );
        })}
        {/* Bars */}
        {monthly.map((m, i) => {
          const gx = PAD_L + i * barGroupW + (barGroupW - barW * 2 - 2) / 2;
          const hC = ((m.current || 0) / maxV) * chartH;
          const hP = ((m.previous || 0) / maxV) * chartH;
          const active = activeMonth === m.month;
          return (
            <g key={m.month} onClick={() => onClickMonth?.(m.month)}
               style={{ cursor: onClickMonth ? "pointer" : "default" }}
               data-testid={`trend-bar-${m.month}`}>
              {/* Previous year bar */}
              <rect
                x={gx} y={PAD_T + chartH - hP} width={barW} height={hP}
                fill="#cbd5e1" rx={2}
                opacity={active ? 1 : 0.85}
              >
                <title>{m.month} {yearPrevious}: {fmt(m.previous)} unidades</title>
              </rect>
              {/* Current year bar */}
              <rect
                x={gx + barW + 2} y={PAD_T + chartH - hC} width={barW} height={hC}
                fill={active ? "#f4c400" : "#111827"} rx={2}
              >
                <title>{m.month} {yearCurrent}: {fmt(m.current)} unidades ({m.delta_pct != null ? `${m.delta_pct > 0 ? "+" : ""}${m.delta_pct}%` : "s/d"} YoY)</title>
              </rect>
              {/* Month label */}
              <text x={gx + barW + 1} y={H - 10} textAnchor="middle" fontSize={10}
                    fill={active ? "#111827" : "#64748b"} fontWeight={800}>
                {m.month}
              </text>
            </g>
          );
        })}
        {/* Linear trend line over current-year bars (drawn last so it sits on top) */}
        {trendPath && (
          <g data-testid="sales-trend-line">
            <path d={trendPath} fill="none" stroke={trendColor} strokeWidth={2.25}
                  strokeDasharray="6 4" strokeLinecap="round"/>
            {/* End-cap dot */}
            {(() => {
              const parts = trendPath.match(/L\s+([-\d.]+)\s+([-\d.]+)/);
              if (!parts) return null;
              return <circle cx={parts[1]} cy={parts[2]} r={3.5} fill={trendColor}/>;
            })()}
          </g>
        )}
      </svg>
      <div className="flex gap-4 flex-wrap mt-1 text-xs font-extrabold text-gray-500 px-2">
        <span><i className="sq" style={{ background: "#111827" }}></i>{yearCurrent}</span>
        <span><i className="sq" style={{ background: "#cbd5e1" }}></i>{yearPrevious}</span>
        {trendPath && (
          <span data-testid="sales-trend-legend">
            <i className="sq" style={{ background: trendColor,
                                        border: `2px dashed ${trendColor}`, background: "transparent" }}></i>
            Tendencia {yearCurrent}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
export default function SalesPerformancePage() {
  const pageRef = useRef(null);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY);
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch { return DEFAULT_FILTERS; }
  });
  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(filters)); } catch { /* noop */ }
  }, [filters]);

  const [options, setOptions] = useState({
    years: [], months: MONTHS.map((m) => ({ value: m })),
    brands: [], showrooms: [], models: [], business_units: [], channels: [],
  });
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [byBrand, setByBrand] = useState(null);
  const [byBU, setByBU] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [byModel, setByModel] = useState(null);
  const [channelAttr, setChannelAttr] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [financing, setFinancing] = useState(null);
  const [ccOverview, setCcOverview] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [dailyTrend, setDailyTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const load = async (f = filters) => {
    setLoading(true);
    setError(null);
    try {
      const g = { channel: f.channel, campaign: f.campaign, executive: f.executive };
      const [opt, s, t, b, u, fn, fc, mdl, ca, al, fin, cc, sb, dt] = await Promise.all([
        api.get("/sales-performance/filter-options", { params: f }),
        api.get("/sales-performance/summary", { params: f }),
        api.get("/sales-performance/trend", { params: { ...g,
          year: f.year, brand: f.brand, model: f.model,
          showroom: f.showroom, business_unit: f.business_unit,
        }}),
        api.get("/sales-performance/by-brand", { params: { ...g,
          year: f.year, month: f.month, showroom: f.showroom,
          business_unit: f.business_unit,
        }}),
        api.get("/sales-performance/by-business-unit", { params: { ...g,
          year: f.year, month: f.month, brand: f.brand, showroom: f.showroom,
        }}),
        api.get("/sales-performance/funnel", { params: f }),
        api.get("/sales-performance/forecast", { params: { ...g,
          brand: f.brand, model: f.model, showroom: f.showroom,
          business_unit: f.business_unit,
        }}),
        api.get("/sales-performance/by-model", { params: { ...g,
          year: f.year, month: f.month, brand: f.brand,
          showroom: f.showroom, business_unit: f.business_unit,
          limit: 200,
        }}),
        api.get("/sales-performance/channel-attribution", { params: f }),
        api.get("/sales-performance/zero-conversion-alerts", { params: { ...g,
          brand: f.brand, business_unit: f.business_unit,
        }}),
        api.get("/sales-performance/financing", { params: f }),
        api.get("/sales-performance/contact-center-overview", { params: { ...g,
          year: f.year, month: f.month, brand: f.brand, model: f.model,
          showroom: f.showroom, business_unit: f.business_unit,
          channel: f.channel,
        }}),
        api.get("/sales-performance/lead-status-breakdown", { params: { ...g,
          // Skip lead_status_group so the pie always renders the full
          // breakdown even while drilled down — otherwise it would
          // collapse to a single 100% slice and hide the exit door.
          year: f.year, month: f.month, brand: f.brand, model: f.model,
          showroom: f.showroom, business_unit: f.business_unit,
          channel: f.channel,
        }}),
        api.get("/sales-performance/daily-trend", { params: {
          year: f.year, month: f.month, brand: f.brand, model: f.model,
          showroom: f.showroom, business_unit: f.business_unit,
          channel: f.channel, campaign: f.campaign,
          executive: f.executive, status: f.status,
        }}),
      ]);
      setOptions(opt.data);
      setSummary(s.data);
      setTrend(t.data);
      setByBrand(b.data);
      setByBU(u.data);
      setFunnel(fn.data);
      setForecast(fc.data);
      setByModel(mdl.data);
      setChannelAttr(ca.data);
      setAlerts(al.data);
      setFinancing(fin.data);
      setCcOverview(cc.data);
      setStatusBreakdown(sb.data);
      setDailyTrend(dt.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filters); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => setFilters(DEFAULT_FILTERS);

  // Export leads CSV — mirrors the CRM Overview behavior. Downloads
  // the full leads_raw dump (filterless, server-side cap of 200k).
  const onExportCSV = async () => {
    const res = await api.get("/leads/export-csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "leads_export.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  // "Actualizando…" pill — same UX pattern used by Dashboard, Scorecard
  // y Predictive: aparece sólo cuando ya hay datos en pantalla y estamos
  // refetching, con un piso de 700ms para evitar parpadeos.
  const showRefetchPill = useMinVisible(loading, 700);

  if (loading && !summary) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-10">
        <div className="surface p-10 text-center">
          <div className="mx-auto rounded-full grid place-items-center mb-3"
               style={{ width: 48, height: 48, background: "#fee2e2", color: "#991b1b" }}>
            <AlertTriangle size={22}/>
          </div>
          <h2 className="font-display text-xl font-extrabold mb-2">Error cargando el dashboard</h2>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button onClick={() => load()} className="btn primary" data-testid="sp-retry">Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="w-full mx-auto p-5" data-testid="sales-performance-page" style={{ maxWidth: "min(100%, 1920px)" }}>
      {/* Row 1 · Header + PDF */}
      <header className="surface flex justify-between items-center gap-4 p-5 mb-5 flex-wrap"
              data-pdf-hide>
        <div className="flex items-center gap-3">
          <div className="kpi-icon-clean" style={{ color: "#dc2626",
            boxShadow: "inset 0 0 0 1px #dc26261f" }}>
            <BarChart3 size={20} strokeWidth={2.25}/>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold font-display leading-none">Sales Performance</h1>
            <p className="text-xs text-gray-500 font-semibold mt-1">
              Command center comercial · Ventas · Funnel · MoM · YoY
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <PdfExportButton
            targetRef={pageRef}
            filename="sales-performance"
            title="SDM · Sales Performance"
            subtitle={`${filters.year || ""} ${filters.month || ""}`.trim()}
            testid="sp-pdf-btn"
          />
          <button className="btn flex items-center gap-2" onClick={onExportCSV} data-testid="sp-export-csv-btn">
            <Download size={16}/> CSV
          </button>
          <button className="btn flex items-center gap-2" onClick={() => load()} data-testid="sp-refresh-btn">
            <RefreshCw size={16}/>
          </button>
          <button className="btn primary" onClick={reset} data-testid="sp-reset-btn">Borrar Filtros</button>
        </div>
      </header>

      {/* Row 2 · Filters */}
      <section className="surface p-4 mb-5" data-pdf-hide>
        <div className="sp-filter-grid-10">
          {[
            ["Año",              "year",          options.years,          "Todos"],
            ["Mes",              "month",         MONTHS.map((m) => ({ value: m })), "Todos"],
            ["Marca",            "brand",         options.brands,         "Todas"],
            ["Modelo",           "model",         options.models,         "Todos"],
            ["Sucursal",         "showroom",      options.showrooms,      "Todas"],
            ["Unidad de Negocio","business_unit", options.business_units, "Todas"],
            ["Ejecutivo",        "executive",     options.executives,     "Todos"],
            ["Estado",           "status",        options.statuses,       "Todos"],
            ["Canal",            "channel",       options.channels,       "Todos"],
            ["Campaña",          "campaign",      options.campaigns,      "Todas"],
          ].map(([label, key, opts, all]) => (
            <div key={key}>
              <label className="label-up block mb-2">{label}</label>
              <MultiSelect
                value={filters[key]}
                onChange={(v) => setF(key, v)}
                placeholder={label}
                options={opts || []}
                allValue={all}
                allLabel={all}
                testid={`sp-filter-${key}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Zero-conversion alerts — removed from the dashboard on user
          request (moved out of the fold). The endpoint and the
          `ZeroConversionAlerts` component are still available for future
          reuse (e.g., a dedicated Alertas page or push notifications). */}

      {/* Row 3a · Contact Center Funnel KPIs — Total / Abiertos / Calificados / Descalificados */}
      {ccOverview && (
        <>
          <div className="flex items-center gap-2 mb-2 mt-1">
            <PhoneCall size={14} strokeWidth={2.4} style={{ color: "#0891b2" }}/>
            <h3 className="label-up text-[11px]" style={{ color: "#0891b2" }}>
              Contact Center · Funnel del Call Center
            </h3>
          </div>
          <TooltipProvider>
          <section className="grid gap-3 mb-5" data-testid="sp-cc-kpi-grid" style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}>
            {[
              ["total_leads", Users, "Total Leads Recibidos", "#0891b2", false],
              ["abiertos", ShoppingCart, "Abiertos en Contact Center", "#f97316", false],
              ["calificados_asignados", CheckCircle2, "Calificados / Asignados a Piso", "#16a34a", false,
                `${fmt(ccOverview.current.asignados_piso)} asignados a piso · ${fmt(ccOverview.current.calificados_cc)} calificados sin piso`],
              ["descalificados", XCircle, "Descalificados en Contact Center", "#dc2626", true],
            ].map(([k, Icon, label, accent, inv, footnote]) => (
              <KpiCard
                key={k}
                icon={Icon}
                footnote={footnote}
                tip={k === "calificados_asignados" ? (
                  <ScorecardInfoTip
                    label="Calificados / Asignados a Piso"
                    formula="Asignados a piso + Calificados en Contact Center sin piso"
                    extra="Asignados a piso: leads con sucursal (salón) asignada. Calificados sin piso: leads que el Contact Center ya calificó pero aún esperan sucursal."
                  />
                ) : null}
                label={label}
                value={fmt(ccOverview.current[k])}
                sub={ccOverview.previous_mom
                    ? `${fmt(ccOverview.previous_mom[k])} mes anterior · ${fmt(ccOverview.previous_yoy?.[k])} año anterior`
                    : ""}
                mom={ccOverview.deltas_mom?.[k]}
                yoy={ccOverview.deltas_yoy?.[k]}
                accent={accent}
                invertedDelta={inv}
                testid={`sp-cc-${k}`}
              />
            ))}
          </section>
          </TooltipProvider>
          {TRADE_IN_ENABLED && String(filters.business_unit || "").toLowerCase() === "seminuevos" && (
            <TradeInCard filters={filters}/>
          )}
        </>
      )}

      {/* Sales Funnel + Lead-Status Pie · positioned directly below the
          Contact Center funnel, replacing the previous "Ventas · Piso
          Comercial" KPI grid. Desktop = 60 / 40 side-by-side; on mobile
          the pie stacks under the funnel. */}
      {funnel && (
        <section className="grid gap-4 mb-5 sp-funnel-row"
                 data-testid="sp-funnel-row">
          <div className="surface p-6" data-testid="sp-funnel-panel">
            <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingCart size={14} strokeWidth={2.4} style={{ color: "#111827" }}/>
                  <h3 className="label-up text-[11px]" style={{ color: "#111827" }}>
                    Ventas · Piso Comercial
                  </h3>
                </div>
                <h2 className="text-xl font-extrabold font-display">Sales Funnel</h2>
                <p className="text-sm text-gray-500 font-semibold mt-1">
                  De Lead a Venta · 6 etapas cross-matcheadas por nombre normalizado desde CRM Leads + Oportunidades + Cotizaciones + Facturación.
                </p>
              </div>
              <div className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">
                Total leads: {fmt(funnel.stages[0]?.value)}
              </div>
            </div>
            <FunnelBars stages={funnel.stages}/>
            <NuevosPorSucursal rows={funnel.nuevos_por_sucursal || []}/>
            {funnel.notes?.mapping_required && (
              <div className="mt-4 p-3 rounded-lg text-xs font-semibold flex gap-2"
                   style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" }}
                   data-testid="sp-funnel-mapping-note">
                <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#94a3b8" }}/>
                <div>
                  <strong>Data unavailable — mapping required.</strong> Las 5 etapas
                  intermedias (Visita al Salón, Test Drive, Cotización, Banco, Pedido)
                  necesitan sincronizar la actividad del CRM Dynamics 365
                  (BusinessProcessFlow / activityparty / quote) para poder calcularse.
                </div>
              </div>
            )}
          </div>

          {statusBreakdown && statusBreakdown.total > 0 && (
            <LeadStatusPie data={statusBreakdown}
                           activeBucket={filters.lead_status_group}
                           onClickBucket={(bucket) => setF("lead_status_group",
                             filters.lead_status_group === bucket ? "Todos" : bucket)}/>
          )}
          <div className="sp-projection-slot" data-testid="sp-projection-slot">
            <ProjectionKpiCard filters={filters}/>
          </div>
        </section>
      )}
      {PENDING_FEATURES && <QualificationTrendChart filters={filters}/>}
      {PENDING_FEATURES && <TestDriveClosingTable filters={filters}/>}

      {/* Channel Attribution + Financing · side-by-side directly below the
          Sales Funnel row to save vertical space (50 / 50 desktop, stack
          on tablet/mobile). */}
      {((channelAttr && channelAttr.total_sales > 0) || (financing && financing.total_sales > 0)) && (
        <div className="grid gap-4 mb-5 sp-attribution-row" data-testid="sp-attribution-row">
          {channelAttr && channelAttr.total_sales > 0 && (
            <ChannelAttributionSection data={channelAttr} filters={filters}/>
          )}
          {financing && financing.total_sales > 0 && (
            <FinancingSection data={financing}/>
          )}
        </div>
      )}
      <BankStatusSection filters={filters}/>

      {/* Row 4 · Sales Trend */}
      {trend && (
        <section className="surface p-6 mb-5" data-testid="sp-trend-panel">
          <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-xl font-extrabold font-display">Tendencia de Ventas · YoY</h2>
              <p className="text-sm text-gray-500 font-semibold mt-1">
                Unidades vendidas por mes · {trend.year_current} vs {trend.year_previous}. Click en un mes para filtrar.
              </p>
            </div>
            <div className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">
              Total {trend.year_current}: {fmt(trend.monthly.reduce((a, m) => a + (m.current || 0), 0))}
            </div>
          </div>
          <SalesTrendChart
            monthly={trend.monthly}
            yearCurrent={trend.year_current}
            yearPrevious={trend.year_previous}
            activeMonth={filters.month}
            onClickMonth={(m) => setF("month", filters.month === m ? "Todos" : m)}
          />
        </section>
      )}

      {/* Row 4b · Daily trend of the current (or filtered) month */}
      {dailyTrend && dailyTrend.daily && dailyTrend.daily.length > 0 && (
        <section className="surface p-6 mb-5" data-testid="sp-daily-trend-panel">
          <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-xl font-extrabold font-display">
                Ritmo diario · {dailyTrend.month} {dailyTrend.year}
              </h2>
              <p className="text-sm text-gray-500 font-semibold mt-1">
                Leads creados por día del mes {dailyTrend.is_current_month ? "en curso" : "seleccionado"} con línea de tendencia lineal.
                {!dailyTrend.is_current_month && filters.month === "Todos" && (
                  <span className="ml-1 italic text-gray-400">Mostrando el último mes con datos.</span>
                )}
              </p>
            </div>
            <div className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">
              Total: {fmt(dailyTrend.total)} · {dailyTrend.today}/{dailyTrend.days_in_month} días
            </div>
          </div>
          <DailyTrendChart data={dailyTrend}/>
        </section>
      )}

      {/* Row 5 · Sales by Brand + Sales by Business Unit */}
      <section className="grid gap-4 mb-5" style={{ gridTemplateColumns: "3fr 2fr" }}>
        {/* Brand + Model combined panel — toggle Marcas ↔ Modelos, plus
            expandable brand rows with top-5 models inline. Replaces the
            old standalone "Top Modelos" panel below. */}
        <BrandModelPanel
          byBrand={byBrand}
          byModel={byModel}
          filters={filters}
          onPickBrand={(brand) => setF("brand", filters.brand === brand ? "Todas" : brand)}
          onPickModel={(brand, model) => {
            const nextBrand = filters.model === model ? "Todas" : brand;
            const nextModel = filters.model === model ? "Todos" : model;
            setFilters((p) => ({ ...p, brand: nextBrand, model: nextModel }));
          }}
        />

        {/* Business Unit cards */}
        <div className="surface p-6" data-testid="sp-bu-panel">
          <h2 className="text-xl font-extrabold font-display">Unidades de Negocio</h2>
          <p className="text-sm text-gray-500 font-semibold mt-1 mb-4">
            Participación de ventas Nuevos vs Seminuevos · click para filtrar.
          </p>
          <div className="space-y-3">
            {(byBU?.business_units || []).map((bu) => {
              const active = filters.business_unit === bu.business_unit;
              const isSemi = bu.business_unit === "Seminuevos";
              const accent = isSemi ? "#8b5cf6" : "#111827";
              return (
                <button
                  key={bu.business_unit}
                  type="button"
                  onClick={() => setF("business_unit", active ? "Todas" : bu.business_unit)}
                  className="w-full text-left surface p-4"
                  data-testid={`sp-bu-card-${bu.business_unit}`}
                  style={{
                    borderLeft: `3px solid ${accent}`,
                    background: active ? "#fffbeb" : "#fff",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display font-extrabold text-sm">{bu.business_unit}</span>
                    <span className="pill text-[10px] font-extrabold"
                          style={{ background: `${accent}15`, color: accent, padding: "2px 8px" }}>
                      {bu.share_pct}% del total
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="label-up text-[10px]">Ventas</div>
                      <div className="font-display font-extrabold text-xl" style={{ letterSpacing: "-0.02em" }}>
                        {fmt(bu.units)}
                      </div>
                    </div>
                    <div>
                      <div className="label-up text-[10px]">Leads</div>
                      <div className="font-display font-extrabold text-xl" style={{ letterSpacing: "-0.02em" }}>
                        {bu.leads == null ? <span className="text-gray-300">—</span> : fmt(bu.leads)}
                      </div>
                    </div>
                    <div>
                      <div className="label-up text-[10px]">Calificados</div>
                      <div className="text-sm font-bold text-gray-700"
                           style={{ fontVariantNumeric: "tabular-nums" }}>
                        {bu.qualified == null ? <span className="text-gray-300">—</span> : fmt(bu.qualified)}
                      </div>
                    </div>
                    <div>
                      <div className="label-up text-[10px]">Conversión</div>
                      <div className="text-sm font-bold" style={{
                        color: bu.conversion == null ? "#cbd5e1" : (bu.conversion >= 5 ? "#16a34a" : "#f97316"),
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {bu.conversion == null ? "—" : `${bu.conversion}%`}
                      </div>
                    </div>
                  </div>
                  {bu.business_unit === "Seminuevos" && (
                    <div className="text-[10px] italic text-gray-400 font-semibold mt-2">
                      Leads y conversión de Seminuevos no disponibles todavía (no se taggean en CRM).
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Row 6 · Model Ranking — merged into BrandModelPanel above (Row 5) */}

      {/* Row 7 · Channel Attribution — moved up next to Financing (see sp-attribution-row) */}

      {/* Row 8 · Financing Penetration — moved up next to Channel Attribution */}

      {/* Row 9 · Funnel (moved to top, right below Contact Center funnel) */}

      {/* Row 10 · Forecast Timeline (moved to the end per user preference) */}
      {forecast && forecast.trend && forecast.trend.length > 0 && (
        <ForecastSection forecast={forecast}/>
      )}
      {showRefetchPill && summary && <RefetchPill/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Daily trend chart — day-by-day units of the current/filtered month
// with a linear regression trend line over the days that have elapsed.
// Days after ``today`` are drawn as ghosted placeholders so the reader
// visually understands "the month isn't over yet".
// ─────────────────────────────────────────────────────────────────
function DailyTrendChart({ data }) {
  const daily = data.daily || [];
  const today = data.today || daily.length;
  const isCurrent = data.is_current_month;
  const maxV = Math.max(1, ...daily.map((d) => d.units || 0));

  const W = 900;
  const H = 220;
  const PAD_L = 40, PAD_R = 10, PAD_T = 28, PAD_B = 26;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const slotW = chartW / daily.length;
  const barW = Math.max(3, slotW - 3);

  // Linear regression over the elapsed portion only (so future days
  // don't drag the trend to zero). If the month is closed use all days.
  const elapsed = daily.filter((d) => d.day <= today && d.units >= 0);
  let trendPath = "";
  let trendSlope = 0;
  if (elapsed.length >= 2) {
    const n = elapsed.length;
    const sumX = elapsed.reduce((a, p) => a + p.day, 0);
    const sumY = elapsed.reduce((a, p) => a + p.units, 0);
    const sumXY = elapsed.reduce((a, p) => a + p.day * p.units, 0);
    const sumX2 = elapsed.reduce((a, p) => a + p.day * p.day, 0);
    const denom = n * sumX2 - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    trendSlope = slope;
    const xAt = (day) => PAD_L + (day - 1) * slotW + slotW / 2;
    const yAt = (val) => PAD_T + chartH - Math.max(0, Math.min(chartH, (val / maxV) * chartH));
    const first = elapsed[0].day;
    const last = elapsed[elapsed.length - 1].day;
    trendPath = `M ${xAt(first)} ${yAt(intercept + slope * first)} L ${xAt(last)} ${yAt(intercept + slope * last)}`;
  }
  const trendColor = trendSlope >= 0 ? "#16a34a" : "#dc2626";

  // Day-label stride so the axis doesn't get crowded (every 3 days
  // works well up to 31-day months).
  const labelEvery = daily.length > 15 ? 3 : 1;

  return (
    <div className="w-full max-w-[1400px] mx-auto" data-testid="daily-trend-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
           preserveAspectRatio="xMidYMid meet"
           style={{ display: "block" }}>
        {/* Y-axis grid */}
        {[0, 0.5, 1].map((f) => {
          const y = PAD_T + chartH * (1 - f);
          return (
            <g key={f}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#e2e8f0"
                    strokeWidth={f === 0 ? 1.5 : 1}
                    strokeDasharray={f === 0 ? "" : "3 3"}/>
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize={10} fill="#94a3b8" fontWeight={700}>
                {Math.round(maxV * f)}
              </text>
            </g>
          );
        })}

        {/* Today marker (vertical dashed line) — only if month is in progress */}
        {isCurrent && today > 0 && today < daily.length && (
          <g>
            <line
              x1={PAD_L + (today - 1) * slotW + slotW / 2}
              y1={PAD_T}
              x2={PAD_L + (today - 1) * slotW + slotW / 2}
              y2={PAD_T + chartH}
              stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3"/>
            <text
              x={PAD_L + (today - 1) * slotW + slotW / 2}
              y={PAD_T - 6}
              textAnchor="middle" fontSize={9} fill="#94a3b8" fontWeight={800}>
              HOY
            </text>
          </g>
        )}

        {/* Bars */}
        {daily.map((d) => {
          const gx = PAD_L + (d.day - 1) * slotW + (slotW - barW) / 2;
          const h = ((d.units || 0) / maxV) * chartH;
          const future = isCurrent && d.day > today;
          const fill = future ? "#e2e8f0" : "#111827";
          const showValue = !future && (d.units || 0) > 0;
          return (
            <g key={d.day} data-testid={`daily-bar-${d.day}`}>
              <rect x={gx} y={PAD_T + chartH - h} width={barW} height={h}
                    fill={fill} rx={2} opacity={future ? 0.7 : 1}>
                <title>Día {d.day}: {fmt(d.units)} leads{future ? " (aún por llegar)" : ""} · acum. {fmt(d.cumulative)}</title>
              </rect>
              {showValue && (
                <text
                  x={gx + barW / 2}
                  y={PAD_T + chartH - h - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fontWeight={800}
                  fill="#0f172a">
                  {fmt(d.units)}
                </text>
              )}
              {d.day % labelEvery === 0 && (
                <text x={gx + barW / 2} y={H - 8}
                      textAnchor="middle" fontSize={9}
                      fill={future ? "#cbd5e1" : "#64748b"}
                      fontWeight={800}>
                  {d.day}
                </text>
              )}
            </g>
          );
        })}

        {/* Trend line (drawn last so it sits on top) */}
        {trendPath && (
          <g data-testid="daily-trend-line">
            <path d={trendPath} fill="none" stroke={trendColor} strokeWidth={2.25}
                  strokeDasharray="6 4" strokeLinecap="round"/>
            {(() => {
              const parts = trendPath.match(/L\s+([-\d.]+)\s+([-\d.]+)/);
              if (!parts) return null;
              return <circle cx={parts[1]} cy={parts[2]} r={3.5} fill={trendColor}/>;
            })()}
          </g>
        )}
      </svg>
      <div className="flex gap-4 flex-wrap mt-1 text-xs font-extrabold text-gray-500 px-2">
        <span><i className="sq" style={{ background: "#111827" }}></i>{isCurrent ? "Días transcurridos" : "Leads por día"}</span>
        {isCurrent && (
          <span><i className="sq" style={{ background: "#e2e8f0" }}></i>Días pendientes</span>
        )}
        {trendPath && (
          <span data-testid="daily-trend-legend">
            <i className="sq" style={{ background: "transparent",
                                        border: `2px dashed ${trendColor}` }}></i>
            Tendencia {trendSlope >= 0 ? "creciente" : "decreciente"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lead-Status Pie — 5-bucket donut of `leads_raw.status_reason` sitting
// next to the Sales Funnel. Buckets: Nuevo · Calificado · Contactado ·
// Descalificado · Ventas Perdidas. Slice titles include the underlying
// raw reasons so hovering reveals the composition.
// ─────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  // Preferred 3-bucket palette (Abierto / Calificado / Descalificado).
  "Abierto":         "#2563eb", // azul
  "Calificado":      "#16a34a", // verde
  "Descalificado":   "#f97316", // naranja
  // Legacy 5-bucket labels kept for back-compat with drill-downs.
  "Nuevo":           "#6b7280", // gris
  "Contactado":      "#2563eb", // azul
  "Ventas Perdidas": "#dc2626", // rojo
  "Otros":           "#94a3b8", // grupo fallback
};

function LeadStatusPie({ data, activeBucket, onClickBucket }) {
  const [open, setOpen] = useState({});
  const buckets = (data.buckets || []).filter((b) => b.value > 0);
  const total = data.total || buckets.reduce((a, b) => a + b.value, 0) || 1;
  // Read-only pie — click-to-filter removed 2026-02-16 per Gabriela.
  // Prop kept for backwards compat so the parent's state doesn't break.
  void activeBucket; void onClickBucket;
  const hasActive = false;

  const R_OUTER = 105, R_INNER = 68, CX = 120, CY = 120;
  let cursor = -Math.PI / 2;
  const segs = buckets.map((b) => {
    const frac = b.value / total;
    const angle = frac * 2 * Math.PI;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const large = angle > Math.PI ? 1 : 0;
    const x1 = CX + R_OUTER * Math.cos(start), y1 = CY + R_OUTER * Math.sin(start);
    const x2 = CX + R_OUTER * Math.cos(end),   y2 = CY + R_OUTER * Math.sin(end);
    const x3 = CX + R_INNER * Math.cos(end),   y3 = CY + R_INNER * Math.sin(end);
    const x4 = CX + R_INNER * Math.cos(start), y4 = CY + R_INNER * Math.sin(start);
    // Full circle needs a two-arc path to render (a single arc from a point
    // to itself renders nothing).
    const d = frac >= 0.9999
      ? `M ${CX} ${CY - R_OUTER} A ${R_OUTER} ${R_OUTER} 0 1 1 ${CX - 0.01} ${CY - R_OUTER} L ${CX - 0.01} ${CY - R_INNER} A ${R_INNER} ${R_INNER} 0 1 0 ${CX} ${CY - R_INNER} Z`
      : `M ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${R_INNER} ${R_INNER} 0 ${large} 0 ${x4} ${y4} Z`;
    return { ...b, d, frac, color: STATUS_COLORS[b.bucket] || "#94a3b8" };
  });

  return (
    <div className="surface p-6" data-testid="sp-lead-status-pie">
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <PieChart size={14} strokeWidth={2.4} style={{ color: "#111827" }}/>
          <h3 className="label-up text-[11px]" style={{ color: "#111827" }}>
            Leads · por Estado
          </h3>
        </div>
        <h2 className="text-lg font-extrabold font-display">Distribución por Estado · Piso</h2>
        <p className="text-xs text-gray-500 font-semibold mt-0.5">
          {fmt(total)} leads asignados a piso (excluye Contact Center) · % sobre el total de leads.
        </p>
      </div>
      <div className="flex flex-col items-center gap-4">
        <div className="relative flex-shrink-0" style={{ width: 240, height: 240 }}>
          <svg viewBox="0 0 240 240" width="240" height="240">
            {segs.map((s) => (
              <path key={s.bucket} d={s.d} fill={s.color}
                    style={{ stroke: "#fff", strokeWidth: 1 }}
                    data-testid={`sp-status-slice-${s.bucket}`}/>
            ))}
            {segs.length === 0 && (
              <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke="#e2e8f0" strokeWidth={R_OUTER - R_INNER}/>
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="font-display font-extrabold leading-none"
                 style={{ fontSize: 30, letterSpacing: "-0.03em", color: "#111827" }}>
              {fmt(total)}
            </div>
            <div className="text-xs font-semibold mt-1" style={{ color: "#64748b" }}>leads totales</div>
          </div>
        </div>
        <ul className="w-full flex flex-col gap-1.5" data-testid="sp-status-legend">
          {segs.map((s) => {
            const flat = (s.reasons || []).filter((r) => r.count > 0);
            const groups = s.groups && s.groups.length > 0
              ? s.groups
              : flat.length > 1 || (flat.length === 1 && flat[0].reason !== s.bucket) ? [{ group: null, reasons: flat }] : [];
            return (
              <li key={s.bucket} data-testid={`sp-status-legend-${s.bucket}`}>
                <button type="button" disabled={!groups.length}
                        onClick={() => setOpen((o) => ({ ...o, [s.bucket]: !o[s.bucket] }))}
                        aria-expanded={!!open[s.bucket]}
                        className={`w-full flex items-center gap-2 text-xs text-left px-1.5 py-1 -mx-1.5 rounded ${groups.length ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"}`}
                        style={{ transition: "background-color 120ms ease" }}
                        data-testid={`sp-status-toggle-${s.bucket}`}>
                  <span className="w-3 flex-shrink-0 text-gray-400">
                    {groups.length > 0 && (open[s.bucket] ? <ChevronDown size={12} strokeWidth={2.6}/> : <ChevronRight size={12} strokeWidth={2.6}/>)}
                  </span>
                  <span className="rounded-full flex-shrink-0" style={{ background: s.color, width: 9, height: 9 }}/>
                  <span className="font-display font-extrabold truncate flex-1" style={{ color: "#111827" }}>{s.bucket}</span>
                  <span className="font-extrabold text-gray-700" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(s.value)}</span>
                  <span className="text-gray-400 font-bold w-12 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{s.share_pct}%</span>
                </button>
                {groups.length > 0 && open[s.bucket] && (
                  <div className="ml-4 pl-2 mb-1 flex flex-col gap-0.5" style={{ borderLeft: `2px solid ${s.color}33` }}
                       data-testid={`sp-status-reasons-${s.bucket}`}>
                    {groups.map((g) => (
                      <div key={g.group || "flat"}>
                        {g.group && (
                          <div className="flex items-center text-[11px] font-extrabold text-gray-600 mt-0.5">
                            <span className="flex-1 truncate">{g.group}</span>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(g.value)}</span>
                            <span className="w-12 text-right text-gray-400">{total ? ((g.value / total) * 100).toFixed(1) : 0}%</span>
                          </div>
                        )}
                        {g.reasons.filter((r) => r.count > 0).map((r) => (
                          <div key={r.reason} className={`flex items-center text-[11px] text-gray-500 font-semibold ${g.group ? "pl-2" : ""}`}
                               data-testid={`sp-status-reason-${s.bucket}-${r.reason}`}>
                            <span className="flex-1 truncate" title={r.reason}>{r.reason}</span>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(r.count)}</span>
                            <span className="w-12 text-right text-gray-400">{total ? ((r.count / total) * 100).toFixed(1) : 0}%</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// Horizontal funnel bars — each stage's width is proportional to its share
// of the initial Leads value. Unavailable stages render as dashed gray
// placeholders.
function FunnelBars({ stages }) {
  const base = stages.find((s) => s.available && s.value != null)?.value || 1;
  return (
    <div className="flex flex-col gap-2">
      {stages.map((s0, idx) => {
        const s = PENDING_FEATURES ? s0 : { ...s0, td_breakdown: undefined, avg_ticket: undefined,
          breakdown: s0.name === "Cotización" ? undefined : s0.breakdown };
        const share = s.available && s.value != null ? Math.max(6, (s.value / base) * 100) : 60;
        const isSale = s.name === "Venta";
        const color = !s.available ? "#e2e8f0"
                   : s.name === "Leads" ? "#111827"
                   : s.name === "Leads Asignados" ? "#3b82f6"
                   : isSale ? "#16a34a"
                   : "#f97316";
        // Build a native-tooltip string for stages that carry a
        // breakdown. Two shapes supported today:
        //  · En Bancos         → [{banco, count, statuses:[{status,count}]}]
        //  · Visita al Salón   → [{medio, count, campaigns:[{campaign,count}]}]
        // Format is autodetected from the first entry's keys so both
        // tooltips share the same hover affordance.
        const td = s.td_breakdown;
        const tdList = (title, rows) => rows.length
          ? `\n${title}\n` + rows.map((r) => `  • ${r.name}: ${fmt(r.count)}`).join("\n") : "";
        const mix = s.status_mix;
        const breakdownTip = mix && mix.total
          ? `Leads Asignados · ${fmt(mix.total)}\n` + mix.items.map((b) =>
              `  • ${b.status}: ${fmt(b.count)} · ${b.pct}%`).join("\n")
          : td && (td.models.length || td.executives.length)
          ? `${s.name} · ${fmt(s.value)}` + tdList("Top 5 modelos", td.models) + tdList("Top 5 ejecutivos", td.executives)
          : (s.breakdown && s.breakdown.length > 0 && "estado" in s.breakdown[0])
          ? `${s.name} · ${fmt(s.value)}\n` + s.breakdown.filter((b) => b.counted !== false).map((b) =>
              `  • ${b.estado}: ${fmt(b.count)} · RD$ ${Number(b.importe || 0).toLocaleString("es-DO", { maximumFractionDigits: 0 })}`
            ).join("\n") +
            `\n  Total: RD$ ${s.breakdown.filter((b) => b.counted !== false).reduce((a, b) => a + (b.importe || 0), 0).toLocaleString("es-DO", { maximumFractionDigits: 0 })}` +
            (s.breakdown.some((b) => b.counted === false) ? "\n\nNo suman:" : "") +
            s.breakdown.filter((b) => b.counted === false).map((b) =>
              `\n  • ${b.estado}: ${fmt(b.count)} · RD$ ${Number(b.importe || 0).toLocaleString("es-DO", { maximumFractionDigits: 0 })}`).join("")
          : (s.breakdown && s.breakdown.length > 0)
          ? (() => {
              const first = s.breakdown[0];
              const outerKey = "banco" in first ? "banco" : ("medio" in first ? "medio" : null);
              const subKey = Array.isArray(first.statuses) ? "statuses"
                           : Array.isArray(first.campaigns) ? "campaigns" : null;
              const subLabelKey = subKey === "statuses" ? "status"
                                : subKey === "campaigns" ? "campaign" : null;
              if (!outerKey) return "";
              const lines = s.breakdown.map((b) => {
                const header = `  • ${b[outerKey]}: ${fmt(b.count)}`;
                const subs = subKey ? (b[subKey] || []).slice(0, 5) : [];
                const sub = subs.length > 0
                  ? "\n" + subs.map((st) => `       – ${st[subLabelKey]}: ${fmt(st.count)}`).join("\n")
                  : "";
                return header + sub;
              });
              return `${s.name} · ${fmt(s.value)}\n` + lines.join("\n") +
                     (s.breakdown.length >= 5 ? `\n(top-5 por volumen)` : "");
            })()
          : "";
        return (
          <div key={s.name} className="flex items-center gap-2 sm:gap-3" data-testid={`sp-funnel-stage-${s.name}`}>
            <div className="w-24 sm:w-44 flex-shrink-0">
              <div className="font-display font-extrabold text-xs sm:text-sm truncate" title={s.name}
                   style={{ color: s.available ? "#111827" : "#94a3b8" }}>
                {s.name}
                {breakdownTip && (
                  <span className="ml-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                    hover · {mix ? "% estados" : td ? "top 5" : "estado" in s.breakdown[0] ? "estados" : `top ${s.breakdown.length}`}
                  </span>
                )}
              </div>
              {s.avg_ticket != null && (
                <div className="text-[10px] sm:text-[11px] font-bold text-emerald-700 truncate" data-testid="sp-funnel-avg-ticket"
                     title={`Importe promedio por cotización ganada: RD$ ${Number(s.avg_ticket).toLocaleString("es-DO", { maximumFractionDigits: 0 })}`}>
                  Ticket prom. RD$ {(s.avg_ticket / 1e6).toLocaleString("es-DO", { maximumFractionDigits: 2 })} M
                </div>
              )}
              {!s.available && (
                <div className="text-[10px] italic text-gray-400 font-semibold">Data unavailable</div>
              )}
              {s.piso_total > 0 && (
                <div className="text-[10px] sm:text-[11px] font-bold text-gray-500 truncate" data-testid="sp-funnel-piso-detail"
                     title={`${fmt(s.piso_total)} leads llegaron a piso · ${fmt(s.descal_piso)} ya fueron descalificados en piso · ${fmt(s.value)} siguen activos`}>
                  {fmt(s.piso_total)} a piso · <span className="text-red-500">{fmt(s.descal_piso)} descalif.</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 relative" style={{ height: 32 }} title={breakdownTip || undefined}>
              {s.piso_total > 0 ? (
                <div className="absolute inset-y-0 left-0 rounded flex overflow-hidden" data-testid="sp-funnel-piso-bar"
                     style={{ width: `${Math.max(6, (s.piso_total / base) * 100)}%` }}>
                  <div className="h-full flex items-center px-2 sm:px-3" data-testid="sp-funnel-piso-bar-active"
                       style={{ width: `${(s.value / s.piso_total) * 100}%`, background: color }}>
                    <span className="hidden sm:inline font-display font-extrabold text-[11px] sm:text-xs whitespace-nowrap text-white"
                          style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(s.value)}</span>
                  </div>
                  <div className="h-full flex items-center justify-end px-2" data-testid="sp-funnel-piso-bar-descal"
                       style={{ flex: 1, background: "#fecaca" }}>
                    <span className="hidden sm:inline font-display font-extrabold text-[10px] sm:text-[11px] whitespace-nowrap text-red-700"
                          style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(s.descal_piso)}</span>
                  </div>
                </div>
              ) : (<>
              <div className="absolute inset-y-0 left-0 rounded flex items-center px-2 sm:px-3"
                   style={{
                     width: `${share}%`,
                     background: color,
                     opacity: s.available ? 1 : 0.35,
                     borderStyle: s.available ? "solid" : "dashed",
                     borderWidth: s.available ? 0 : 1,
                     borderColor: "#94a3b8",
                   }}>
                {share >= 18 && (
                  <span className="font-display font-extrabold text-[11px] sm:text-xs whitespace-nowrap"
                        style={{ color: s.available ? "#fff" : "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
                    {s.available ? fmt(s.value) : "—"}
                  </span>
                )}
              </div>
              {share < 18 && s.available && (
                <div className="absolute inset-y-0 flex items-center pl-1 font-display font-extrabold text-[11px] sm:text-xs whitespace-nowrap"
                     style={{ left: `${share}%`, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                  {fmt(s.value)}
                </div>
              )}
              </>)}
            </div>
            <div className="w-10 sm:w-20 text-right text-[11px] sm:text-xs font-bold text-gray-500"
                 style={{ fontVariantNumeric: "tabular-nums" }}>
              {s.share_pct != null ? `${s.share_pct}%` : "—"}
            </div>
            <DropOffCell stage={s} prev={(() => {
              const p = stages.slice(0, idx).reverse().find((x) => x.available && x.value > 0);
              return p && p.piso_total ? { ...p, name: "Llegaron a piso", value: p.piso_total } : p;
            })()}/>
          </div>
        );
      })}
    </div>
  );
}

function DropOffCell({ stage: s, prev }) {
  if (!PENDING_FEATURES) {
    return (
      <div className="w-12 sm:w-24 text-right text-[11px] sm:text-xs font-bold"
           style={{ color: s.drop_off_pct != null && s.drop_off_pct > 50 ? "#dc2626" : "#64748b", fontVariantNumeric: "tabular-nums" }}
           title="Drop-off desde la etapa anterior">
        {s.drop_off_pct != null ? `↓${s.drop_off_pct}%` : ""}
      </div>
    );
  }
  if (s.drop_off_pct == null || !prev) return <div className="w-12 sm:w-24"/>;
  const grows = s.drop_off_pct < 0;
  const pct = Math.abs(s.drop_off_pct);
  const color = grows ? "#94a3b8" : s.drop_off_pct > 50 ? "#dc2626" : "#64748b";
  return (
    <div className="relative group w-12 sm:w-24 text-right text-[11px] sm:text-xs font-bold cursor-help"
         style={{ color, fontVariantNumeric: "tabular-nums" }} data-testid={`sp-funnel-dropoff-${s.name}`}>
      <span className="border-b border-dotted border-current">{grows ? `↑ +${pct}%` : `↓${pct}%`}</span>
      <div className="absolute right-0 bottom-full mb-2 z-30 w-64 p-2.5 rounded-lg bg-gray-900 text-white text-left
                      text-[11px] font-semibold leading-snug shadow-xl opacity-0 pointer-events-none
                      translate-y-1 group-hover:opacity-100 group-hover:translate-y-0"
           style={{ transition: "opacity 150ms ease, transform 150ms ease" }}
           data-testid={`sp-funnel-dropoff-tip-${s.name}`}>
        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-extrabold mb-1">
          {grows ? "Aumento vs etapa anterior" : "Caída vs etapa anterior"}
        </div>
        {s.name} <b>{fmt(s.value)}</b> vs {prev.name} <b>{fmt(prev.value)}</b>
        <div className="mt-1" style={{ color: grows ? "#cbd5e1" : "#fca5a5" }}>
          {grows ? `Crece ${pct}%` : `Se pierde el ${pct}%`}
        </div>
        {grows && (
          <div className="mt-1 text-[10px] text-gray-400">
            Incluye registros que no pasaron por {prev.name}.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Forecast section — historic timeseries + 3-month projection.
// Mirrors the Predictive dashboard's chart with the same visual
// language (solid line for history, dashed for forecast, ±1σ band)
// but scoped to the Sales Performance filter model.
// ─────────────────────────────────────────────────────────────────
function ForecastSection({ forecast }) {
  const { trend, forecast: proj, forecast_avg, last_month_actual, last_month_label,
          current_month: cm, delta_next_month_pct, method } = forecast;
  return (
    <section className="surface p-6 mb-5" data-testid="sp-forecast-panel">
      <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-xl font-extrabold font-display">Forecast · Proyección 3 meses</h2>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Serie histórica ({trend.length} meses) + proyección lineal a {proj.length} meses.
          </p>
        </div>
        <div className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider max-w-[240px] text-right">
          {method}
        </div>
      </div>

      {/* Forecast KPI mini-cards */}
      <div className="grid gap-3 mb-4" style={{
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      }}>
        <ForecastKpi
          label="Último mes cerrado"
          value={fmt(last_month_actual)}
          sub={last_month_label || trend[trend.length - 1]?.label}
          accent="#111827"
          testid="sp-forecast-last"
        />
        {cm && (
          <ForecastKpi
            label={cm.projected ? `Mes en curso · proyección W${cm.current_week ?? "?"}` : "Mes en curso · parcial"}
            value={cm.projected ? fmt(cm.sales) : fmt(cm.actual_to_date)}
            sub={cm.projected
              ? `${cm.label} · ${fmt(cm.actual_to_date)} facturadas · obj ${fmt(cm.target)}`
              : `${cm.label} · sin proyección semanal, fuera del cálculo`}
            accent="#f97316"
            testid="sp-forecast-current"
          />
        )}
        <ForecastKpi
          label="Próximo mes (proyectado)"
          value={fmt(proj[0]?.expected_sales)}
          sub={proj[0]?.label}
          delta={delta_next_month_pct}
          accent="#dc2626"
          testid="sp-forecast-next"
        />
        <ForecastKpi
          label={`Promedio ${proj.length}m proyectado`}
          value={fmt(forecast_avg)}
          sub={`${proj[0]?.label} → ${proj[proj.length - 1]?.label}`}
          accent="#f97316"
          testid="sp-forecast-avg"
        />
      </div>

      <ForecastChart trend={trend} forecast={proj}/>

      <div className="mt-3 flex gap-4 flex-wrap text-xs font-extrabold text-gray-500 px-2">
        <span><i className="sq" style={{ background: "#111827" }}></i>Histórico</span>
        {cm && <span><i className="sq" style={{ background: "#f97316" }}></i>Mes en curso (proyección semanal del equipo)</span>}
        <span><i className="sq" style={{ background: "#dc2626" }}></i>Proyección</span>
        <span><i className="sq" style={{ background: "#fecaca" }}></i>Banda ±1σ (incertidumbre)</span>
      </div>
    </section>
  );
}

function ForecastKpi({ label, value, sub, delta, accent, testid }) {
  return (
    <div className="p-3 rounded-lg" data-testid={testid}
         style={{ background: "#f8fafc", borderLeft: `3px solid ${accent}` }}>
      <div className="label-up text-[10px] mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-display font-extrabold" style={{ fontSize: 26, letterSpacing: "-0.02em", color: accent }}>
          {value}
        </div>
        {delta != null && (
          <DeltaPill value={delta} testid={`${testid}-delta`}/>
        )}
      </div>
      {sub && <div className="text-[11px] text-gray-500 font-semibold mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function ForecastChart({ trend, forecast }) {
  // Full series: history + forecast, sequential x axis. Y auto-scaled.
  const all = [
    ...trend.map((t) => ({ ...t, kind: "hist", value: t.sales, lower: null, upper: null })),
    ...forecast.map((f) => ({ ...f, kind: "fcst", value: f.expected_sales,
                              lower: f.lower_bound, upper: f.upper_bound })),
  ];
  const maxV = Math.max(1, ...all.map((p) => Math.max(p.value || 0, p.upper || 0)));

  const W = 1000, H = 260;
  const PAD_L = 48, PAD_R = 20, PAD_T = 20, PAD_B = 40;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const stepX = chartW / Math.max(1, all.length - 1);

  const xAt = (i) => PAD_L + i * stepX;
  const yAt = (v) => PAD_T + chartH - (v / maxV) * chartH;

  // Split into two polylines: history (solid) and forecast (dashed).
  const histPoints = all.filter((p) => p.kind === "hist");
  const fcstPoints = all.filter((p) => p.kind === "fcst");
  const bridgeIndex = histPoints.length - 1;

  const lastHist = histPoints[histPoints.length - 1];
  const curOpen = lastHist && (lastHist.projected || lastHist.partial);
  const solid = curOpen ? histPoints.slice(0, -1) : histPoints;
  const histPath = solid.map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i)} ${yAt(p.value)}`).join(" ");
  const curPath = curOpen && lastHist.projected && solid.length
    ? `M ${xAt(solid.length - 1)} ${yAt(solid[solid.length - 1].value)} L ${xAt(bridgeIndex)} ${yAt(lastHist.value)}`
    : "";
  // Forecast line starts at the last historic point so it visually connects.
  const fcstPath = fcstPoints.length
    ? `M ${xAt(bridgeIndex)} ${yAt(histPoints[bridgeIndex].value)} ` +
      fcstPoints.map((p, i) => `L ${xAt(bridgeIndex + 1 + i)} ${yAt(p.value)}`).join(" ")
    : "";

  // ±1σ band (uncertainty area) — only over forecast portion.
  const bandTop = fcstPoints.map((p, i) => `${xAt(bridgeIndex + 1 + i)},${yAt(p.upper)}`).join(" ");
  const bandBot = fcstPoints.slice().reverse().map((p, k) => {
    const i = fcstPoints.length - 1 - k;
    return `${xAt(bridgeIndex + 1 + i)},${yAt(p.lower)}`;
  }).join(" ");
  const bandPoly = bandTop && bandBot
    ? `${xAt(bridgeIndex)},${yAt(histPoints[bridgeIndex].value)} ${bandTop} ${bandBot}`
    : "";

  // Sparse x-axis labels — every 3rd month to avoid clutter.
  const showEvery = Math.max(1, Math.floor(all.length / 8));

  return (
    <div className="w-full max-w-[1400px] mx-auto" data-testid="sp-forecast-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
           preserveAspectRatio="xMidYMid meet"
           style={{ display: "block" }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = PAD_T + chartH * (1 - f);
          return (
            <g key={f}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#e2e8f0"
                    strokeWidth={f === 0 ? 1.5 : 1} strokeDasharray={f === 0 ? "" : "3 3"}/>
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize={10} fill="#94a3b8" fontWeight={700}>
                {Math.round(maxV * f)}
              </text>
            </g>
          );
        })}
        {/* Forecast band */}
        {bandPoly && (
          <polygon points={bandPoly} fill="#fecaca" opacity={0.4}/>
        )}
        {/* Divider vertical line between history and forecast */}
        {fcstPoints.length > 0 && (
          <line
            x1={xAt(bridgeIndex)} y1={PAD_T} x2={xAt(bridgeIndex)} y2={PAD_T + chartH}
            stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4"
          />
        )}
        {/* History path */}
        <path d={histPath} fill="none" stroke="#111827" strokeWidth={2.5}/>
        {curPath && <path d={curPath} fill="none" stroke="#f97316" strokeWidth={2.5} strokeDasharray="3 3"/>}
        {/* Forecast path */}
        <path d={fcstPath} fill="none" stroke="#dc2626" strokeWidth={2.5} strokeDasharray="6 4"/>
        {/* Dots */}
        {all.map((p, i) => (
          <g key={`${p.year}-${p.month}-${i}`}>
            <circle cx={xAt(i)} cy={yAt(p.value)} r={p.projected ? 5.5 : 3.5}
                    fill={p.partial ? "#fff" : p.projected ? "#f97316" : p.kind === "hist" ? "#111827" : "#dc2626"}
                    stroke={p.partial ? "#94a3b8" : "none"} strokeWidth={p.partial ? 2 : 0}
                    data-testid={p.projected ? "sp-forecast-current-dot" : undefined}/>
            {p.projected && (
              <text x={xAt(i)} y={yAt(p.value) - 10} textAnchor="middle" fontSize={11} fontWeight={800} fill="#f97316">
                {Math.round(p.value)}
              </text>
            )}
            <title>{p.projected
              ? `${p.label}: ${Math.round(p.value)} proyección W${p.current_week} (${(p.weekly || []).slice(0, p.current_week || 5).map((v, k) => `W${k + 1} ${Math.round(v)}`).join(" · ")}) · ${p.actual_to_date} facturadas`
              : p.partial ? `${p.label}: ${p.actual_to_date} facturadas a la fecha (parcial, fuera del cálculo)`
              : `${p.label}: ${Math.round(p.value)} ${p.kind === "fcst" ? "(proj)" : ""}`}</title>
          </g>
        ))}
        {/* X-axis labels */}
        {all.map((p, i) => {
          if (i % showEvery !== 0 && i !== all.length - 1) return null;
          return (
            <text key={`x-${i}`} x={xAt(i)} y={H - 18} textAnchor="middle"
                  fontSize={10} fill="#64748b" fontWeight={700}>
              {p.month}
            </text>
          );
        })}
        {/* Year strip (below month labels) */}
        {all.map((p, i) => {
          if (i === 0 || p.year !== all[i - 1].year) {
            return (
              <text key={`y-${i}`} x={xAt(i)} y={H - 4} textAnchor="middle"
                    fontSize={9} fill="#94a3b8" fontWeight={800}>
                {p.year}
              </text>
            );
          }
          return null;
        })}
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Zero-conversion alerts — actionable banner surfaced above the fold
// when a channel or campaign is bleeding leads without landing a sale.
// Severity colour-coded (high red, medium amber, low slate).
// ─────────────────────────────────────────────────────────────────
function ZeroConversionAlerts({ alerts }) {
  const sevMap = {
    high:   { color: "#991b1b", bg: "#fee2e2", label: "Alto" },
    medium: { color: "#92400e", bg: "#fef3c7", label: "Medio" },
    low:    { color: "#475569", bg: "#f1f5f9", label: "Bajo" },
  };
  const channel = alerts.channel_alerts || [];
  const campaign = alerts.campaign_alerts || [];
  if (channel.length === 0 && campaign.length === 0) return null;
  return (
    <section className="mb-5" data-testid="sp-alerts-panel">
      <div className="surface p-4" style={{ borderLeft: "4px solid #dc2626" }}>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="kpi-icon-clean" style={{ color: "#dc2626", boxShadow: "inset 0 0 0 1px #dc26261f" }}>
            <Bell size={18} strokeWidth={2.25}/>
          </div>
          <div className="flex-1">
            <h3 className="font-display font-extrabold text-base">Alerta · Canales sin conversión</h3>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              Últimos {alerts.window_days} días · ≥ {alerts.min_leads} leads · <strong>cero ventas atribuidas</strong>. Requiere revisión inmediata.
            </p>
          </div>
        </div>

        {channel.length > 0 && (
          <div className="mb-3">
            <div className="label-up text-[10px] mb-2">Canales</div>
            <div className="flex flex-wrap gap-2">
              {channel.map((a) => {
                const s = sevMap[a.severity] || sevMap.low;
                return (
                  <div key={a.name}
                       className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5"
                       data-testid={`sp-alert-channel-${a.name}`}
                       style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}30` }}>
                    <AlertTriangle size={13} strokeWidth={2.4}/>
                    <span className="text-xs font-extrabold">{a.name}</span>
                    <span className="text-[10px] font-bold opacity-75">·</span>
                    <span className="text-xs font-extrabold" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {fmt(a.leads)} leads
                    </span>
                    <span className="text-[10px] font-bold opacity-75">·</span>
                    <span className="text-xs font-extrabold">0 ventas</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {campaign.length > 0 && (
          <div>
            <div className="label-up text-[10px] mb-2">
              Campañas con alto volumen sin conversión matcheada (informativo)
            </div>
            <div className="flex flex-wrap gap-2">
              {campaign.slice(0, 8).map((a) => {
                const s = sevMap[a.severity] || sevMap.low;
                return (
                  <div key={a.name}
                       className="inline-flex items-center gap-2 rounded px-2 py-1"
                       data-testid={`sp-alert-campaign-${a.name}`}
                       style={{ background: s.bg, color: s.color }}
                       title={a.name}>
                    <span className="text-[11px] font-extrabold truncate" style={{ maxWidth: 180 }}>{a.name}</span>
                    <span className="text-[10px] font-bold opacity-75">·</span>
                    <span className="text-[11px] font-extrabold" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {fmt(a.leads)} leads
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Model Ranking — top-10 model bar rows with MoM/YoY deltas.
// Click a row to filter the whole dashboard by that model.
// ─────────────────────────────────────────────────────────────────
function BrandModelPanel({ byBrand, byModel, filters, onPickBrand, onPickModel }) {
  const [view, setView] = useState("brands"); // "brands" | "models"
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [expandAllFor, setExpandAllFor] = useState(new Set()); // per-brand "ver todos"

  const brands = byBrand?.brands || [];
  const models = byModel?.models || [];

  // Group models per brand for the expandable panel (case-insensitive
  // brand match so "NISSAN" ≈ "Nissan").
  const modelsByBrand = new Map();
  for (const m of models) {
    const key = (m.brand || "").toLowerCase();
    if (!modelsByBrand.has(key)) modelsByBrand.set(key, []);
    modelsByBrand.get(key).push(m);
  }
  for (const arr of modelsByBrand.values()) arr.sort((a, b) => b.units - a.units);

  const totalUnits = view === "brands" ? byBrand?.total : byModel?.total;

  return (
    <div className="surface p-6" data-testid="sp-brand-panel">
      <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-extrabold font-display">
            {view === "brands" ? "Ventas por Marca" : "Ventas por Modelo"}
          </h2>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            {view === "brands"
              ? `Top ${brands.length} marcas · click en la fila para desplegar los modelos.`
              : `Top ${models.length} modelos del período · click para filtrar todo el dashboard.`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">
            {fmt(totalUnits || 0)} uds
          </div>
          {/* Toggle pill */}
          <div className="inline-flex rounded-full p-0.5"
               style={{ background: "#f1f5f9" }}
               data-testid="sp-brand-model-toggle">
            {["brands", "models"].map((v) => (
              <button key={v}
                      type="button"
                      onClick={() => { setView(v); setExpandedBrand(null); }}
                      className="px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition"
                      style={{
                        background: view === v ? "#111827" : "transparent",
                        color: view === v ? "#fff" : "#64748b",
                      }}
                      data-testid={`sp-brand-model-tab-${v}`}>
                {v === "brands" ? "Marcas" : "Modelos"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === "brands" ? (
        <div className="flex flex-col">
          <div className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
               style={{ gridTemplateColumns: "18px 24px 48px minmax(0, 1fr) 90px 80px 80px 80px",
                        color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}>
            <span></span>
            <span>#</span>
            <span></span>
            <span>Marca</span>
            <span className="text-right">Unidades</span>
            <span className="text-right">Share</span>
            <span className="text-right">MoM</span>
            <span className="text-right">YoY</span>
          </div>
          {brands.map((b, i) => {
            const active = filters.brand === b.brand;
            const isExpanded = expandedBrand === b.brand;
            const brandModels = modelsByBrand.get((b.brand || "").toLowerCase()) || [];
            const showAll = expandAllFor.has(b.brand);
            const visibleModels = showAll ? brandModels : brandModels.slice(0, 5);
            const hasMore = brandModels.length > 5;
            return (
              <div key={b.brand} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <div className="grid items-center gap-3 px-3 hover:bg-slate-50 text-left cursor-pointer"
                     data-testid={`sp-brand-row-${b.brand}`}
                     onClick={() => setExpandedBrand(isExpanded ? null : b.brand)}
                     style={{
                       gridTemplateColumns: "18px 24px 48px minmax(0, 1fr) 90px 80px 80px 80px",
                       paddingTop: 12, paddingBottom: 12,
                       background: active ? "#fffbeb" : "transparent",
                       borderLeft: active ? "2px solid #f4c400" : "2px solid transparent",
                     }}>
                  <span style={{ color: "#94a3b8" }} data-testid={`sp-brand-caret-${b.brand}`}>
                    {isExpanded
                      ? <ChevronDown size={14} strokeWidth={2.5}/>
                      : <ChevronRight size={14} strokeWidth={2.5}/>}
                  </span>
                  <span className="font-display font-extrabold text-sm" style={{ color: "#64748b" }}>#{i + 1}</span>
                  <ModelThumb brand={b.brand} model="" imageUrl={b.image_url}/>
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="font-display font-extrabold text-sm truncate" title={b.brand}>{b.brand}</span>
                    {brandModels.length > 0 && (
                      <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full"
                            style={{ background: "#f1f5f9", color: "#64748b" }}
                            title={`${brandModels.length} modelos con ventas`}>
                        {brandModels.length} mod.
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onPickBrand(b.brand); }}
                      className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full hover:bg-yellow-100"
                      style={{ background: active ? "#fef3c7" : "#f8fafc",
                               color: active ? "#92400e" : "#64748b" }}
                      title="Filtrar dashboard por esta marca"
                      data-testid={`sp-brand-filter-${b.brand}`}>
                      {active ? "✓ filtrando" : "filtrar"}
                    </button>
                  </div>
                  <span className="text-right font-display font-extrabold text-sm"
                        style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(b.units)}</span>
                  <span className="text-right text-xs font-bold text-gray-500"
                        style={{ fontVariantNumeric: "tabular-nums" }}>{b.share_pct}%</span>
                  <span className="text-right"><DeltaPill value={b.mom_pct}/></span>
                  <span className="text-right"><DeltaPill value={b.yoy_pct}/></span>
                </div>

                {isExpanded && (
                  <div className="pb-3 pl-6 pr-3"
                       style={{ background: "#fafbfc" }}
                       data-testid={`sp-brand-expand-${b.brand}`}>
                    {brandModels.length === 0 ? (
                      <div className="py-3 text-xs italic text-gray-400 font-semibold">
                        Sin modelos con ventas de esta marca en el período.
                      </div>
                    ) : (
                      <>
                        <div className="grid items-center gap-3 px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-wider"
                             style={{ gridTemplateColumns: "40px minmax(0, 1fr) 70px 60px 60px 60px",
                                      color: "#94a3b8" }}>
                          <span></span>
                          <span>Modelo</span>
                          <span className="text-right">Uds</span>
                          <span className="text-right">Share</span>
                          <span className="text-right">MoM</span>
                          <span className="text-right">YoY</span>
                        </div>
                        {visibleModels.map((m) => {
                          const mActive = filters.model === m.model;
                          return (
                            <button
                              key={`${m.brand}-${m.model}`}
                              type="button"
                              onClick={() => onPickModel(m.brand, m.model)}
                              className="grid items-center gap-3 px-3 hover:bg-white text-left rounded"
                              data-testid={`sp-brand-model-row-${m.model}`}
                              style={{
                                gridTemplateColumns: "40px minmax(0, 1fr) 70px 60px 60px 60px",
                                paddingTop: 8, paddingBottom: 8,
                                background: mActive ? "#fffbeb" : "transparent",
                                borderLeft: mActive ? "2px solid #f4c400" : "2px solid transparent",
                              }}>
                              <ModelThumb brand={m.brand} model={m.model} imageUrl={m.image_url}/>
                              <span className="font-display font-extrabold text-xs truncate" title={m.model}>
                                {m.model}
                              </span>
                              <span className="text-right font-display font-extrabold text-xs"
                                    style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(m.units)}</span>
                              <span className="text-right text-[10px] font-bold text-gray-500"
                                    style={{ fontVariantNumeric: "tabular-nums" }}>{m.share_pct}%</span>
                              <span className="text-right"><DeltaPill value={m.mom_pct}/></span>
                              <span className="text-right"><DeltaPill value={m.yoy_pct}/></span>
                            </button>
                          );
                        })}
                        {hasMore && !showAll && (
                          <button
                            type="button"
                            onClick={() => setExpandAllFor((prev) => new Set(prev).add(b.brand))}
                            className="mt-1 text-[10px] font-extrabold uppercase tracking-wider hover:underline"
                            style={{ color: "#0891b2" }}
                            data-testid={`sp-brand-show-all-${b.brand}`}>
                            Ver todos los {brandModels.length} modelos →
                          </button>
                        )}
                        {hasMore && showAll && (
                          <button
                            type="button"
                            onClick={() => setExpandAllFor((prev) => {
                              const s = new Set(prev); s.delete(b.brand); return s;
                            })}
                            className="mt-1 text-[10px] font-extrabold uppercase tracking-wider hover:underline"
                            style={{ color: "#64748b" }}>
                            Ver solo top 5 ↑
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {brands.length === 0 && (
            <div className="text-sm text-gray-500 py-8 text-center font-semibold">
              Sin ventas para estos filtros.
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
               style={{ gridTemplateColumns: "24px 48px 90px minmax(0, 1fr) 70px 60px 60px 60px",
                        color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}>
            <span>#</span>
            <span></span>
            <span>Marca</span>
            <span>Modelo</span>
            <span className="text-right">Uds</span>
            <span className="text-right">Share</span>
            <span className="text-right">MoM</span>
            <span className="text-right">YoY</span>
          </div>
          <div style={{ maxHeight: 560, overflowY: "auto" }}>
            {models.map((m, i) => {
              const mActive = filters.model === m.model;
              return (
                <button
                  key={`${m.brand}-${m.model}`}
                  type="button"
                  onClick={() => onPickModel(m.brand, m.model)}
                  className="grid items-center gap-3 px-3 hover:bg-slate-50 text-left w-full"
                  data-testid={`sp-model-row-${m.model}`}
                  style={{ gridTemplateColumns: "24px 48px 90px minmax(0, 1fr) 70px 60px 60px 60px",
                           paddingTop: 10, paddingBottom: 10,
                           borderBottom: "1px solid #f1f5f9",
                           background: mActive ? "#fffbeb" : "transparent",
                           borderLeft: mActive ? "2px solid #f4c400" : "2px solid transparent" }}>
                  <span className="font-display font-extrabold text-sm" style={{ color: "#64748b" }}>#{i + 1}</span>
                  <ModelThumb brand={m.brand} model={m.model} imageUrl={m.image_url}/>
                  <span className="text-xs font-bold text-gray-500 truncate" title={m.brand}>{m.brand}</span>
                  <span className="font-display font-extrabold text-sm truncate" title={m.model}>{m.model}</span>
                  <span className="text-right font-display font-extrabold text-sm"
                        style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(m.units)}</span>
                  <span className="text-right text-xs font-bold text-gray-500"
                        style={{ fontVariantNumeric: "tabular-nums" }}>{m.share_pct}%</span>
                  <span className="text-right"><DeltaPill value={m.mom_pct}/></span>
                  <span className="text-right"><DeltaPill value={m.yoy_pct}/></span>
                </button>
              );
            })}
            {models.length === 0 && (
              <div className="text-sm text-gray-500 py-8 text-center font-semibold">
                Sin ventas para estos filtros.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModelRankingSection({ data, activeModel, onClickModel }) {
  return (
    <section className="surface p-6 mb-5" data-testid="sp-model-panel">
      <div className="flex justify-between items-baseline flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Car size={18} strokeWidth={2.25} style={{ color: "#0891b2" }}/>
            <h2 className="text-xl font-extrabold font-display">Top {data.limit} Modelos</h2>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Qué vehículos específicos empujan las cifras · click para filtrar todo el dashboard.
          </p>
        </div>
        <div className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">
          {fmt(data.total)} unidades totales
        </div>
      </div>
      <div className="flex flex-col">
        <div className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
             style={{ gridTemplateColumns: "24px 48px 90px minmax(0, 1fr) minmax(0, 1.2fr) 70px 70px 70px 70px",
                      color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}>
          <span>#</span>
          <span></span>
          <span>Marca</span>
          <span>Modelo</span>
          <span>Top Vendedor</span>
          <span className="text-right">Uds</span>
          <span className="text-right">Share</span>
          <span className="text-right">MoM</span>
          <span className="text-right">YoY</span>
        </div>
        {data.models.map((m, i) => {
          const active = activeModel === m.model;
          const tv = m.top_vendor;
          const tvShare = m.top_vendor_share;
          const tvUnits = m.top_vendor_units;
          return (
            <button
              key={`${m.brand}-${m.model}`}
              type="button"
              onClick={() => onClickModel?.(m.model)}
              className="grid items-center gap-3 px-3 hover:bg-slate-50 text-left"
              data-testid={`sp-model-row-${m.model}`}
              style={{ gridTemplateColumns: "24px 48px 90px minmax(0, 1fr) minmax(0, 1.2fr) 70px 70px 70px 70px",
                       paddingTop: 12, paddingBottom: 12,
                       borderBottom: "1px solid #f1f5f9",
                       background: active ? "#fffbeb" : "transparent",
                       borderLeft: active ? "2px solid #f4c400" : "2px solid transparent" }}
            >
              <span className="font-display font-extrabold text-sm" style={{ color: "#64748b" }}>#{i + 1}</span>
              <ModelThumb brand={m.brand} model={m.model} imageUrl={m.image_url}/>
              <span className="text-xs font-bold text-gray-500 truncate" title={m.brand}>{m.brand}</span>
              <span className="font-display font-extrabold text-sm truncate" title={m.model}>{m.model}</span>
              <div className="min-w-0" title={tv || ""}>
                {tv ? (
                  <>
                    <div className="text-xs font-extrabold text-gray-800 truncate" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {tv.split(" ").slice(0, 2).join(" ")}
                    </div>
                    <div className="text-[10px] text-gray-500 font-bold">
                      {tvUnits}u ({tvShare}%) · {m.vendor_count} vendedores
                    </div>
                  </>
                ) : <span className="text-xs text-gray-400">—</span>}
              </div>
              <span className="text-right font-display font-extrabold text-sm"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(m.units)}</span>
              <span className="text-right text-xs font-bold text-gray-500"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{m.share_pct}%</span>
              <span className="text-right"><DeltaPill value={m.mom_pct}/></span>
              <span className="text-right"><DeltaPill value={m.yoy_pct}/></span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Channel Attribution — sales share by lead origin (email/phone match).
// SVG donut + compact table with conversion % per channel.
// ─────────────────────────────────────────────────────────────────
const CH_COLORS = {
  "Meta / Social":   "#3b82f6",
  "Google / Search": "#dc2626",
  "Web / SEO":       "#16a34a",
  "Walk-in":         "#f4c400",
  "Referido":        "#a855f7",
  "Otro":            "#64748b",
};

function ChannelAttributionSection({ data, filters }) {
  const [drillChannel, setDrillChannel] = useState(null);
  const rows = (data.channels || []).filter((c) => c.sales > 0);
  const totalAttr = data.attributed_sales || 1;

  const R_OUTER = 78, R_INNER = 50, CX = 90, CY = 90;
  let cursor = -Math.PI / 2;
  const segs = rows.map((r) => {
    const frac = r.sales / totalAttr;
    const angle = frac * 2 * Math.PI;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const large = angle > Math.PI ? 1 : 0;
    const x1 = CX + R_OUTER * Math.cos(start), y1 = CY + R_OUTER * Math.sin(start);
    const x2 = CX + R_OUTER * Math.cos(end),   y2 = CY + R_OUTER * Math.sin(end);
    const x3 = CX + R_INNER * Math.cos(end),   y3 = CY + R_INNER * Math.sin(end);
    const x4 = CX + R_INNER * Math.cos(start), y4 = CY + R_INNER * Math.sin(start);
    const d = `M ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${R_INNER} ${R_INNER} 0 ${large} 0 ${x4} ${y4} Z`;
    return { ...r, d, frac, color: CH_COLORS[r.channel] || "#64748b" };
  });

  return (
    <section className="surface mb-5" data-testid="sp-channel-attr-panel" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
        <div className="flex items-center gap-2">
          <PieChart size={18} strokeWidth={2.25} style={{ color: "#8b5cf6" }}/>
          <h2 className="text-xl font-extrabold font-display">Atribución Lead → Sale por Canal</h2>
        </div>
        <p className="text-sm text-gray-500 font-semibold mt-1">
          Cross-match email/teléfono lead↔venta · {fmt(data.attributed_sales)} de {fmt(data.total_sales)} ventas atribuidas
          {data.unattributed_sales > 0 && (
            <> · <span title="Ventas sin lead digital asociado">{fmt(data.unattributed_sales)} sin atribución ({data.unattributed_pct}%)</span></>
          )}
        </p>
      </div>
      <div className="grid gap-6" style={{ gridTemplateColumns: "220px 1fr", padding: "20px 24px", alignItems: "start" }}>
        {/* Donut */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: 180, height: 180 }}>
            <svg viewBox="0 0 180 180" width="180" height="180">
              {segs.map((s) => (
                <path key={s.channel} d={s.d} fill={s.color}
                      onClick={() => setDrillChannel(s.channel)}
                      style={{ cursor: "pointer" }}>
                  <title>{s.channel}: {s.sales} ventas ({(s.frac * 100).toFixed(1)}%) · click drill</title>
                </path>
              ))}
              {segs.length === 0 && (
                <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke="#e2e8f0" strokeWidth={R_OUTER - R_INNER}/>
              )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display font-extrabold leading-none"
                   style={{ fontSize: 22, letterSpacing: "-0.03em", color: "#111827" }}>
                {fmt(data.attributed_sales)}
              </div>
              <div className="text-xs font-semibold" style={{ color: "#64748b" }}>ventas atribuidas</div>
            </div>
          </div>
        </div>

        {/* Compact table */}
        <div className="flex flex-col" data-testid="sp-channel-attr-list">
          <div className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
               style={{ gridTemplateColumns: "minmax(0,1fr) 90px 70px 80px 80px",
                        color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}>
            <span>Canal</span>
            <span className="text-right">Leads</span>
            <span className="text-right">Ventas</span>
            <span className="text-right">Conv.</span>
            <span className="text-right">Share</span>
          </div>
          {(data.channels || []).map((c) => {
            const color = CH_COLORS[c.channel] || "#64748b";
            const convColor = c.conversion_pct >= 5 ? "#16a34a" : c.conversion_pct >= 1 ? "#f97316" : "#dc2626";
            const canDrill = c.sales > 0;
            return (
              <button key={c.channel}
                   type="button"
                   onClick={() => canDrill && setDrillChannel(c.channel)}
                   disabled={!canDrill}
                   className="grid items-center gap-3 px-3 text-left"
                   data-testid={`sp-channel-row-${c.channel}`}
                   style={{ gridTemplateColumns: "minmax(0,1fr) 90px 70px 80px 80px",
                            paddingTop: 10, paddingBottom: 10,
                            borderBottom: "1px solid #f1f5f9",
                            cursor: canDrill ? "pointer" : "default",
                            background: canDrill ? undefined : "transparent" }}
                   title={canDrill ? "Click para ver campañas + UTMs + modelos" : ""}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="rounded-full flex-shrink-0" style={{ background: color, width: 9, height: 9 }}/>
                  <span className="font-display font-extrabold text-sm truncate" title={c.channel}>{c.channel}</span>
                  {canDrill && <span className="text-[9px] text-gray-400 font-bold">↗ drill</span>}
                </div>
                <span className="text-right text-sm font-extrabold text-gray-500"
                      style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.leads)}</span>
                <span className="text-right font-display font-extrabold text-sm"
                      style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.sales)}</span>
                <span className="text-right text-sm font-extrabold"
                      style={{ color: convColor, fontVariantNumeric: "tabular-nums" }}>
                  {c.conversion_pct}%
                </span>
                <span className="text-right text-xs font-bold text-gray-500"
                      style={{ fontVariantNumeric: "tabular-nums" }}>{c.share_of_sales}%</span>
              </button>
            );
          })}
          {data.unattributed_sales > 0 && (
            <div className="grid items-center gap-3 px-3 mt-1 rounded"
                 style={{ gridTemplateColumns: "minmax(0,1fr) 90px 70px 80px 80px",
                          paddingTop: 10, paddingBottom: 10, background: "#f8fafc",
                          border: "1px dashed #cbd5e1" }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="rounded-full flex-shrink-0" style={{ background: "#cbd5e1", width: 9, height: 9 }}/>
                <span className="font-display font-extrabold text-sm text-gray-500">Sin atribución digital</span>
              </div>
              <span className="text-right text-sm font-extrabold text-gray-400">—</span>
              <span className="text-right font-display font-extrabold text-sm text-gray-500"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(data.unattributed_sales)}</span>
              <span className="text-right text-sm font-extrabold text-gray-400">—</span>
              <span className="text-right text-xs font-bold text-gray-500"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{data.unattributed_pct}%</span>
            </div>
          )}
          <div className="text-[10px] italic text-gray-400 font-semibold mt-3">
            "Sin atribución" = ventas cuyo email/teléfono no matchea con ningún lead digital.
            Provienen de walk-in sin registro, referidos telefónicos o clientes recurrentes.
          </div>
        </div>
      </div>
      {drillChannel && (
        <ChannelDrillModal channel={drillChannel} filters={filters}
                            onClose={() => setDrillChannel(null)}/>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Channel Drill-Down Modal — top campaigns, UTM sources, and models
// for sales attributed to a single channel.
// ─────────────────────────────────────────────────────────────────
function ChannelDrillModal({ channel, filters, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    api.get("/sales-performance/channel-drill", { params: { channel, ...filters }})
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message); });
    return () => { alive = false; };
  }, [channel, filters]);

  const color = CH_COLORS[channel] || "#64748b";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15, 23, 42, 0.75)" }}
         onClick={onClose}
         data-testid="sp-channel-drill-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col"
           style={{ maxWidth: 820, maxHeight: "90vh" }}
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between"
             style={{ background: "#0f172a", color: "#fff" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ width: 40, height: 40, background: color, color: "#0f172a" }}>
              <PieChart size={20} strokeWidth={2.4}/>
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-lg leading-tight">
                {channel}
              </div>
              <div className="text-xs font-semibold opacity-75 mt-0.5">
                Drill-down · campañas, UTMs y modelos que aportaron ventas
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data && data.attributed_sales > 0 && (
              <button onClick={() => exportDrillCsv(data, channel)}
                      data-testid="sp-drill-csv-btn"
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-extrabold"
                      style={{ background: color, color: "#0f172a" }}>
                <Download size={13} strokeWidth={2.4}/>
                CSV
              </button>
            )}
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10"
                    data-testid="sp-channel-drill-close">
              <XCircle size={22}/>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-auto">
          {!data && !error && (
            <div className="py-16 text-center text-sm text-gray-500 font-semibold">
              Cargando drill-down…
            </div>
          )}
          {error && (
            <div className="p-3 rounded-lg text-xs font-bold" style={{ background: "#fee2e2", color: "#991b1b" }}>
              Error: {error}
            </div>
          )}
          {data && (
            <>
              {/* KPI strip */}
              <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <DrillKpi label="Leads" value={fmt(data.leads)} accent="#111827"/>
                <DrillKpi label="Ventas Atribuidas" value={fmt(data.attributed_sales)} accent={color}/>
                <DrillKpi label="Conversión"
                          value={`${data.conversion_pct}%`}
                          accent={data.conversion_pct >= 5 ? "#16a34a" : data.conversion_pct >= 1 ? "#f97316" : "#dc2626"}/>
              </div>

              {data.attributed_sales === 0 && (
                <div className="p-3 rounded-lg text-xs font-semibold flex gap-2 mb-4"
                     style={{ background: "#fef3c7", color: "#92400e" }}>
                  <AlertTriangle size={14} className="flex-shrink-0"/>
                  <span>Sin ventas atribuidas a este canal en el período filtrado.</span>
                </div>
              )}

              {data.attributed_sales > 0 && (
                <>
                  <DrillTable
                    title="Top Campañas"
                    rows={data.top_campaigns}
                    testidPrefix="sp-drill-campaign"
                    accent={color}
                  />
                  <DrillTable
                    title="Top UTM Sources"
                    rows={data.top_utm_sources}
                    testidPrefix="sp-drill-utm-source"
                    accent={color}
                    emptyLabel="Sin utm_source en las ventas atribuidas (leads directos o sin tracking UTM)."
                  />
                  <DrillTable
                    title="Top UTM Campaigns"
                    rows={data.top_utm_campaigns}
                    testidPrefix="sp-drill-utm-campaign"
                    accent={color}
                    emptyLabel="Sin utm_campaign en las ventas atribuidas."
                  />
                  <DrillTable
                    title="Top Modelos"
                    rows={data.top_models.map((m) => ({ ...m, name: `${m.brand} ${m.model}` }))}
                    testidPrefix="sp-drill-model"
                    accent={color}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DrillKpi({ label, value, accent }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "#f8fafc", borderLeft: `3px solid ${accent}` }}>
      <div className="label-up text-[10px] mb-1">{label}</div>
      <div className="font-display font-extrabold" style={{ fontSize: 22, letterSpacing: "-0.02em", color: accent }}>
        {value}
      </div>
    </div>
  );
}

function DrillTable({ title, rows, testidPrefix, accent, emptyLabel }) {
  if (!rows || rows.length === 0) {
    if (emptyLabel) {
      return (
        <div className="mb-4">
          <div className="label-up mb-2">{title}</div>
          <div className="text-xs text-gray-400 font-semibold italic p-2">{emptyLabel}</div>
        </div>
      );
    }
    return null;
  }
  const max = Math.max(1, ...rows.map((r) => r.sales));
  return (
    <div className="mb-5">
      <div className="label-up mb-2">{title}</div>
      <div className="flex flex-col gap-1">
        {rows.map((r) => {
          const w = Math.max(3, (r.sales / max) * 100);
          return (
            <div key={r.name} className="grid items-center gap-3"
                 style={{ gridTemplateColumns: "minmax(0,1fr) 60px 60px" }}
                 data-testid={`${testidPrefix}-${r.name}`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-extrabold text-gray-800 truncate" title={r.name}>{r.name}</div>
                  <div className="relative mt-1" style={{ height: 4, background: "#f1f5f9", borderRadius: 2 }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0,
                                  width: `${w}%`, background: accent, borderRadius: 2, opacity: 0.85 }}/>
                  </div>
                </div>
              </div>
              <span className="text-right font-display font-extrabold text-sm"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(r.sales)}</span>
              <span className="text-right text-xs font-bold text-gray-500"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{r.share_pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CSV export for the drill-down modal — one flat file with `section`
// column so managers can drop it straight into Excel / Google Sheets.
// ─────────────────────────────────────────────────────────────────
function exportDrillCsv(data, channel) {
  const rows = [
    ["section", "name", "sales", "share_pct"],
    ["kpi", "Leads", data.leads, ""],
    ["kpi", "Ventas Atribuidas", data.attributed_sales, ""],
    ["kpi", "Conversión %", data.conversion_pct, ""],
  ];
  const push = (section, arr, nameKey = "name") => {
    (arr || []).forEach((r) => rows.push([section, r[nameKey] || r.name, r.sales, r.share_pct]));
  };
  push("campaign", data.top_campaigns);
  push("utm_source", data.top_utm_sources);
  push("utm_campaign", data.top_utm_campaigns);
  (data.top_models || []).forEach((m) => {
    rows.push(["model", `${m.brand} ${m.model}`, m.sales, m.share_pct]);
  });

  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `drill_${channel.replace(/[^\w]+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─────────────────────────────────────────────────────────────────
// Financing Penetration — pie of tipo_compra + banks placeholder
// ─────────────────────────────────────────────────────────────────
const FIN_COLORS = {
  "Crédito":         "#3b82f6",
  "F. Externo":      "#0891b2",
  "F. Interno":      "#8b5cf6",
  "Facilidad":       "#f4c400",
  "Contado":         "#16a34a",
  "Exonerada":       "#94a3b8",
  "Dealer":          "#64748b",
  "Vta. Directa":    "#64748b",
};

function FinancingSection({ data }) {
  const rows = (data.breakdown || []).filter((r) => r.count > 0);
  const R_OUTER = 78, R_INNER = 50, CX = 90, CY = 90;
  let cursor = -Math.PI / 2;
  const segs = rows.map((r) => {
    const frac = r.count / data.total_sales;
    const angle = frac * 2 * Math.PI;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const large = angle > Math.PI ? 1 : 0;
    const x1 = CX + R_OUTER * Math.cos(start), y1 = CY + R_OUTER * Math.sin(start);
    const x2 = CX + R_OUTER * Math.cos(end),   y2 = CY + R_OUTER * Math.sin(end);
    const x3 = CX + R_INNER * Math.cos(end),   y3 = CY + R_INNER * Math.sin(end);
    const x4 = CX + R_INNER * Math.cos(start), y4 = CY + R_INNER * Math.sin(start);
    const d = `M ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${R_INNER} ${R_INNER} 0 ${large} 0 ${x4} ${y4} Z`;
    return { ...r, d, frac, color: FIN_COLORS[r.name] || "#64748b" };
  });

  return (
    <section className="surface mb-5" data-testid="sp-financing-panel"
             style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
        <div className="flex items-center gap-2">
          <Wallet size={18} strokeWidth={2.25} style={{ color: "#0891b2" }}/>
          <h2 className="text-xl font-extrabold font-display">Penetración de Financiamiento</h2>
        </div>
        <p className="text-sm text-gray-500 font-semibold mt-1">
          {data.penetration_pct}% de las ventas fueron financiadas · {data.cash_pct}% al contado
        </p>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: "180px 1fr", padding: "20px 24px", alignItems: "start" }}>
        {/* Donut */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: 160, height: 160 }}>
            <svg viewBox="0 0 180 180" width="160" height="160">
              {segs.map((s) => (
                <path key={s.name} d={s.d} fill={s.color}>
                  <title>{s.name}: {s.count} ventas ({s.share_pct}%)</title>
                </path>
              ))}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display font-extrabold leading-none"
                   style={{ fontSize: 24, letterSpacing: "-0.03em", color: "#0891b2" }}>
                {data.penetration_pct}%
              </div>
              <div className="text-[10px] font-extrabold text-gray-500 uppercase tracking-wider mt-0.5">
                financiamiento
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown table */}
        <div className="flex flex-col" data-testid="sp-financing-breakdown">
          <div className="label-up text-[10px] mb-2">Distribución por Tipo</div>
          <div className="grid items-center gap-2 px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider"
               style={{ gridTemplateColumns: "minmax(0,1fr) 70px 60px",
                        color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}>
            <span>Tipo</span>
            <span className="text-right">Ventas</span>
            <span className="text-right">Share</span>
          </div>
          {rows.map((r) => (
            <div key={r.name}
                 className="grid items-center gap-2 px-2"
                 data-testid={`sp-financing-row-${r.name}`}
                 style={{ gridTemplateColumns: "minmax(0,1fr) 70px 60px",
                          paddingTop: 8, paddingBottom: 8, borderBottom: "1px solid #f1f5f9" }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="rounded-full flex-shrink-0" style={{ background: FIN_COLORS[r.name] || "#64748b", width: 8, height: 8 }}/>
                <div className="min-w-0">
                  <div className="text-xs font-extrabold text-gray-800 truncate" title={r.name}>{r.name}</div>
                  <div className="text-[9px] text-gray-500 font-bold uppercase tracking-wider">{r.group}</div>
                </div>
              </div>
              <span className="text-right text-sm font-extrabold"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(r.count)}</span>
              <span className="text-right text-xs font-bold text-gray-500"
                    style={{ fontVariantNumeric: "tabular-nums" }}>{r.share_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Model thumbnail — scraped SDM catalog image with brand-initial
// fallback when either the URL is missing or the image fails to load.
// The palette matches the site's color language so the fallback
// still reads as "on brand".
// ─────────────────────────────────────────────────────────────────
const BRAND_COLORS = {
  NISSAN:    { bg: "#c3002f", fg: "#fff" },
  CHEVROLET: { bg: "#f4c400", fg: "#111" },
  SUZUKI:    { bg: "#003a70", fg: "#fff" },
  YAMAHA:    { bg: "#0044b3", fg: "#fff" },
  INFINITI:  { bg: "#1e1e1e", fg: "#fff" },
  CADILLAC:  { bg: "#111827", fg: "#fff" },
  FORD:      { bg: "#003478", fg: "#fff" },
  TOYOTA:    { bg: "#eb0a1e", fg: "#fff" },
  HYUNDAI:   { bg: "#002c5f", fg: "#fff" },
};

function ModelThumb({ brand, model, imageUrl }) {
  const [broken, setBroken] = useState(false);
  const palette = BRAND_COLORS[(brand || "").toUpperCase()] || { bg: "#e2e8f0", fg: "#475569" };
  const showFallback = !imageUrl || broken;
  return (
    <div className="relative flex-shrink-0" style={{ width: 40, height: 28 }}
         data-testid={`sp-model-thumb-${model}`}>
      {showFallback ? (
        <div className="rounded flex items-center justify-center font-display font-extrabold"
             style={{
               width: 40, height: 28,
               background: palette.bg, color: palette.fg,
               fontSize: 11, letterSpacing: "-0.02em",
               border: "1px solid rgba(0,0,0,.06)",
             }}
             title={brand}>
          {(brand || "?").slice(0, 3).toUpperCase()}
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={`${brand} ${model}`}
          loading="lazy"
          onError={() => setBroken(true)}
          style={{
            width: 40, height: 28, objectFit: "contain",
            borderRadius: 4, background: "#f8fafc",
            border: "1px solid rgba(0,0,0,.06)",
          }}
        />
      )}
    </div>
  );
}
